import React from "react";
import PropTypes from "prop-types";
import { Utilities } from "@learningblocks/exp-constants";
import { StartRow } from "../StartRow";
import { EndRow } from "../EndRow";
import { HexagonNodeHolder } from "../HexagonNodeHolder";
import "./style.scss";

const { SLIDE_NODE, checkIfNodeComplete } = Utilities;
const NODE_STATES = {
  EMPTY: "EMPTY",
  CURRENT: "CURRENT",
  COMPLETE: "COMPLETE",
};

/**
 * JointGroup: A wrapper component in design section.
 * The component wraps around DesignSlide Components that joins
 * box-like components. It is also responsible for rendering the
 * StartNode and EndNode of DesignSection
 */
export function JointGroup(props) {
  const setNodeState = (node) => {
    const nodeComplete = checkIfNodeComplete(node);
    let nodeState = NODE_STATES.EMPTY;
    if (node.current) nodeState = NODE_STATES.CURRENT;
    if (nodeComplete) nodeState = NODE_STATES.COMPLETE;
    return nodeState;
  };

  const renderNormalHexagonNodeHolder = () =>
    React.createElement(HexagonNodeHolder, {
      positionId: NaN,
      overrides: props.overrides && props.overrides.HEXAGON_CARD,
    });

  const renderGroupNode = (groupNode) => {
    switch (groupNode.nodeType) {
      case SLIDE_NODE.START_NODE:
        return (
          <StartRow
            key={groupNode.nodeId}
            {...groupNode}
            nodeHolder={renderNormalHexagonNodeHolder()}
          />
        );
      case SLIDE_NODE.END_NODE:
        return <EndRow key={groupNode.nodeId} {...groupNode} />;
      case SLIDE_NODE.JOINT_DRAG_DROP:
        return React.cloneElement(props.jointDragDropRow, {
          ...groupNode,
          key: groupNode.nodeId,
          nodeState: setNodeState(groupNode),
          nodeHolder: React.cloneElement(
            props.jointDragDropRow.props.nodeHolder,
            {
              current: groupNode.current,
            }
          ),
        });
      default:
        return null;
    }
  };

  return (
    <div className="jointGroup">{props.groupNodes.map(renderGroupNode)}</div>
  );
}

JointGroup.propTypes = {
  /**
   * Unique Id for the group
   */
  groupId: PropTypes.number.isRequired,
  /**
   * List of all Components to be displayed inside the Group
   */
  groupNodes: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Booelean for specifying if current node.
       */
      current: PropTypes.bool,
      /**
       * List of components to be displayed in GroupRow
       */
      nodeComponents: PropTypes.array,
      /**
       * Object contains error messages for incorrect drops,
       * based on position of element
       */
      nodeFeedback: PropTypes.object,
      /**
       * Unique id for the Node
       */
      nodeId: PropTypes.number.isRequired,
      /**
       * The type of node.
       */
      nodeType: PropTypes.oneOf([
        SLIDE_NODE.START_NODE,
        SLIDE_NODE.JOINT_DRAG_DROP,
        SLIDE_NODE.END_NODE,
      ]).isRequired,
    })
  ),
  /**
   * DragDrop Row Coomponent for HexagonCard nodes.
   */
  jointDragDropRow: PropTypes.element.isRequired,
  /**
   * Override component that is rendered
   */
  overrides: PropTypes.object,
};

JointGroup.defaultProsps = {
  groupId: null,
  groupType: null,
  groupNodes: [],
  overrides: {},
};
